// Ersatz-Formular für die LP-Anmeldung: Klaviyo rendert eingebettete Formulare nur mit
// Cookie-Zustimmung (oder gar nicht, z. B. bei Werbeblockern). Dann trägt dieses Formular
// über die öffentliche Klaviyo-Client-API direkt in dieselbe Liste ein.
// Block-Scope: Die Datei kann mehrfach eingebunden sein (eine Section pro Vorkommen).
{
  class LpSignup extends HTMLElement {
    static KLAVIYO_TIMEOUT = 3000;

    connectedCallback() {
      this.klaviyoTarget = this.querySelector('[data-klaviyo-target]');
      this.fallback = this.querySelector('[data-fallback]');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-status]');

      this.form.addEventListener('submit', (event) => this.submit(event));

      if (!this.klaviyoTarget) {
        this.showFallback();
        return;
      }

      new MutationObserver(() => this.onKlaviyoRendered()).observe(this.klaviyoTarget, { childList: true });

      // Ohne Zustimmung kommt kein Klaviyo-Formular, also sofort das eigene zeigen. Mit Zustimmung
      // auf Klaviyo warten (Statistik), aber nicht ewig, falls es z. B. ein Werbeblocker verhindert.
      this.whenConsentKnown((marketingAllowed) => {
        if (marketingAllowed) {
          setTimeout(() => this.showFallback(), LpSignup.KLAVIYO_TIMEOUT);
        } else {
          this.showFallback();
        }
      });
    }

    whenConsentKnown(callback) {
      const privacy = () => window.Shopify?.customerPrivacy;
      if (privacy()) {
        callback(privacy().marketingAllowed());
      } else if (window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) =>
          callback(!error && Boolean(privacy()?.marketingAllowed()))
        );
      } else {
        callback(false);
      }
    }

    klaviyoRendered() {
      return Boolean(this.klaviyoTarget?.children.length);
    }

    onKlaviyoRendered() {
      if (!this.klaviyoRendered()) return;
      // Hat der Besucher schon im Ersatz-Formular getippt, bleibt es stehen, damit nichts verloren geht.
      if (this.fallbackTouched()) {
        this.klaviyoTarget.hidden = true;
      } else {
        this.fallback.hidden = true;
      }
    }

    fallbackTouched() {
      return [...this.form.elements].some((field) => (field.type === 'checkbox' ? field.checked : field.value));
    }

    showFallback() {
      if (this.klaviyoRendered()) return;
      this.fallback.hidden = false;
    }

    async submit(event) {
      event.preventDefault();
      const data = new FormData(this.form);
      const properties = { [this.dataset.consentProperty]: this.dataset.consentValue };
      if (data.get('newsletter')) properties.newsletter_optin = 'ja';
      const interests = data.getAll('interest');
      if (interests.length) properties[this.dataset.interestProperty] = interests;

      this.setBusy(true);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${this.dataset.companyId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.source,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: data.get('email'), first_name: data.get('first_name'), properties },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: this.dataset.listId } } },
            },
          }),
        });
        if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
        this.form.hidden = true;
        this.querySelector('[data-success]').hidden = false;
      } catch (error) {
        console.error(error);
        this.setBusy(false);
        this.status.textContent = this.dataset.errorMessage;
      }
    }

    setBusy(busy) {
      this.form.querySelector('button').disabled = busy;
      this.status.textContent = '';
    }
  }

  if (!customElements.get('lp-signup')) customElements.define('lp-signup', LpSignup);
}
